import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import services from 'services/services';

// Import utilities
import { openNotification } from 'components/utilities/notification/notification';

// Import types
import type { AgreementsFormValues, AgreementsSubmitHandler } from 'types/views/agreements';

import type { SubscriptionCreateParams } from 'helpers/api/packetsSubscription';

import { usePayment } from 'components/views/offer_configurator/hooks/usePayment';

export const useRegulationModal = (
	agreementsIds: number[],
	defaultValues: AgreementsFormValues,
	isStateLoading: boolean,
	params: SubscriptionCreateParams
) => {
	const { t } = useTranslation('configurator');
	const payment = usePayment();
	const methods = useForm<AgreementsFormValues>({
		defaultValues
	});

	useEffect(() => {
		!isStateLoading && methods.reset({ all: defaultValues.all, agreement_version_ids: defaultValues.agreement_version_ids });
	}, [isStateLoading]);

	useEffect(() => {
		const subscription = methods.watch((values, { name, type }) => {
			if (!type) return;
			if (name === 'all') {
				const value = values.all ? agreementsIds : [];
				methods.setValue('agreement_version_ids', value, { shouldValidate: methods.formState.isSubmitted });
			} else if (name === 'agreement_version_ids') {
				const value = agreementsIds.every((id) => values.agreement_version_ids?.includes(id));
				methods.setValue('all', value);
			}
		});

		return () => subscription.unsubscribe();
	}, [agreementsIds]);

	const agreements = useMutation({
		mutationFn: (data: AgreementsFormValues) => services.post('/subscriber/agreements/accept', data),
		onSuccess: () => payment.mutate(params),
		onError: () =>
			openNotification({
				type: 'error',
				title: t('notification_error_title'),
				description: t('agreements_error_description')
			})
	});

	const onSubmit: AgreementsSubmitHandler = (data) => agreements.mutate(data);

	const isSubmitting = agreements.isLoading || payment.isLoading;

	return {
		methods,
		onSubmit,
		isSubmitting
	};
};
